import type { Time } from "lightweight-charts";
import { useMemo } from "react";

import { type ChartSettings, sampleConfig, type TFilterMinMax } from "../../../../../data/live-charts/config";
import { filterMinMax } from "../../utils/filter";
import type { CandleData } from "../../context";

import { useHistorical } from "./useHistorical";
import { useLive } from "./useLive";

export { useCandlesCache } from "./useHistorical";

export const useChartData = ({
  chart,
  enabled,
  filter,
}: {
  chart:
    | Partial<
        Pick<
          ChartSettings,
          "productId" | "sampleTime" | "periodFrom" | "artisType" | "editable" | "id" | "expression" | "indicatorsState"
        >
      >
    | undefined;
  enabled?: boolean;
  filter?: TFilterMinMax;
}) => {
  const { historical, onLive } = useHistorical(chart, enabled);

  const sampleSeconds = sampleConfig[chart?.sampleTime ?? "10s"].seconds;

  useLive(chart, (data) => onLive(data), enabled);

  const candles = useMemo(() => {
    const existing = new Set<number>();
    const items: CandleData[] = [];
    for (const page of historical.data?.pages ?? []) {
      for (const item of page.data ?? []) {
        if (existing.has(item[0])) continue;
        existing.add(item[0]);

        // 0: time, 1: open, 2: close, 3: low, 4: high
        items.push({
          time: (item[0] / 1000) as Time,
          open: item[1],
          close: item[2],
          low: item[3],
          high: item[4],
          gap: false,
        });
      }
    }

    let sorted = items.sort((a, b) => (a.time as number) - (b.time as number));

    // Check for time gaps between consecutive items
    sorted = sorted.map((item, index) => {
      if (index === 0) return item;
      if (index > 0) {
        const gap = (item.time as number) - (sorted[index - 1].time as number);
        return { ...item, gap: gap > sampleSeconds };
      }
      return item;
    });

    if (filter) {
      return filterMinMax(filter, sorted);
    }

    return sorted;
  }, [historical.data, filter, sampleSeconds]);

  return {
    historical,
    hasNextPage: historical.hasNextPage,
    candles,
  };
};
